import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  loadImplementationSummary,
  selectImplementationSummary,
  selectImplementationSummaryStatus,
  selectImplementationSyncStatus,
  syncImplementationProgressThunk,
} from '../features/iso9001/implementation/isoImplementationSlice';
import { progressBarClass } from '../features/iso9001/implementation/isoImplementationUtils';
import { fetchImplGap } from '../features/ai/aiApi';
import {
  ISO_IMPLEMENTATION_META,
  type IsoImplementationStandard,
} from '../features/iso9001/implementation/isoImplementationConstants';

const AUTO_SYNC_DEBOUNCE_MS = 1500;

function resolveGuidePath(standard: IsoImplementationStandard, slug: string): string | null {
  const meta = ISO_IMPLEMENTATION_META[standard];
  if (slug === 'guia' && meta.guidePath) return meta.guidePath;
  if (slug === 'hoja-ruta' && meta.roadmapPath) return meta.roadmapPath;
  if (slug === 'entregables' && meta.deliverablesPath) return meta.deliverablesPath;
  const annex = meta.annexes?.find((a) => a.guideSlug === slug);
  return annex?.path ?? null;
}

interface IsoImplementationProgressPageProps {
  standard: IsoImplementationStandard;
}

export function IsoImplementationProgressPage({ standard }: IsoImplementationProgressPageProps) {
  const meta = ISO_IMPLEMENTATION_META[standard];
  const { canEdit: canEditStandards } = usePlatformAccessLevel('admin.standards');
  const dispatch = useDispatch<AppDispatch>();
  const summary = useSelector(selectImplementationSummary(standard));
  const status = useSelector(selectImplementationSummaryStatus);
  const syncStatus = useSelector(selectImplementationSyncStatus);
  const autoSyncTriggered = useRef(false);
  const [gapAnalyzing, setGapAnalyzing] = useState(false);
  const [gapDraftSummary, setGapDraftSummary] = useState<string | null>(null);

  const runSync = useCallback(() => {
    void dispatch(syncImplementationProgressThunk(standard));
  }, [dispatch, standard]);

  useEffect(() => {
    void dispatch(loadImplementationSummary(standard));
  }, [dispatch, standard]);

  useEffect(() => {
    if (status !== 'succeeded' || autoSyncTriggered.current) return;

    const timer = window.setTimeout(() => {
      autoSyncTriggered.current = true;
      runSync();
    }, AUTO_SYNC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [status, runSync]);

  if (status === 'loading' && !summary) {
    return <p className="p-6 text-sm text-muted-foreground">Cargando avance de implementación…</p>;
  }

  if (!summary) {
    return (
      <p className="p-6 text-sm text-destructive">
        No se pudo cargar el resumen de implementación {meta.label}.
      </p>
    );
  }

  const isSyncing = syncStatus === 'loading';

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-6">
      <header className="space-y-3 border-b border-border pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Centro de implementación
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">{meta.label} — Avance</h1>
            <p className="text-sm text-muted-foreground">
              {summary.completedTasks} de {summary.totalTasks} tareas completadas ·{' '}
              {summary.pendingManual} pendientes manuales ·{' '}
              {summary.pendingAutomatic.length} pendientes automáticos
            </p>
          </div>
          <button
            type="button"
            onClick={runSync}
            disabled={isSyncing}
            className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium shadow-sm transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSyncing ? 'Sincronizando…' : 'Sincronizar evidencia'}
          </button>
          <button
            type="button"
            disabled={gapAnalyzing}
            onClick={async () => {
              setGapAnalyzing(true);
              setGapDraftSummary(null);
              try {
                const result = await fetchImplGap({ standard });
                const summary =
                  (result.draft.summary as string | undefined) ?? result.text.trim();
                setGapDraftSummary(summary);
              } catch {
                setGapDraftSummary('No se pudo generar el análisis de brechas IA.');
              } finally {
                setGapAnalyzing(false);
              }
            }}
            className="inline-flex shrink-0 items-center justify-center rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary disabled:opacity-50"
          >
            {gapAnalyzing ? 'Analizando…' : 'Análisis brechas IA'}
          </button>
        </div>
      </header>

      {gapDraftSummary && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-xs font-medium text-amber-900 dark:text-amber-100 mb-2">
            Análisis brechas IA — referencia, no modifica el avance
          </p>
          <p className="whitespace-pre-wrap">{gapDraftSummary}</p>
        </div>
      )}

      {canEditStandards ? (
        <section className="rounded-lg border border-primary/20 bg-primary/5 p-4 md:p-5">
          <p className="text-sm text-muted-foreground">
            Las normas ISO activas del tenant se configuran en{' '}
            <Link to="/normas-iso" className="font-medium text-primary underline-offset-4 hover:underline">
              Administración → Normas ISO
            </Link>
            .
          </p>
        </section>
      ) : null}

      <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium">Avance global</span>
          <span className="font-mono font-semibold">{summary.overallPercent}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full transition-all ${progressBarClass(summary.overallPercent)}`}
            style={{ width: `${summary.overallPercent}%` }}
          />
        </div>
      </section>

      {summary.pendingAutomatic.length > 0 && (
        <section className="space-y-3 rounded-lg border border-amber-200/80 bg-amber-50/50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
          <h2 className="text-lg font-semibold">
            Pendiente automático (sin evidencia en plataforma)
          </h2>
          <ul className="space-y-2">
            {summary.pendingAutomatic.map((item) => (
              <li
                key={item.taskId}
                className="flex flex-col gap-1 rounded-md border border-border/60 bg-card px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  {item.clauseRef ? (
                    <span className="font-mono text-xs text-muted-foreground">{item.clauseRef} — </span>
                  ) : null}
                  {item.title}
                </span>
                {item.platformRoute ? (
                  <Link
                    to={item.platformRoute}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    Ir al módulo
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid gap-4 md:grid-cols-2">
        {summary.guides.map((guide) => {
          const guidePath = resolveGuidePath(standard, guide.slug);
          const card = (
            <>
              <h2 className="font-semibold">{guide.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {guide.completedTasks}/{guide.totalTasks} · {guide.percent}%
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full ${progressBarClass(guide.percent)}`}
                  style={{ width: `${guide.percent}%` }}
                />
              </div>
            </>
          );

          if (!guidePath) {
            return (
              <div key={guide.slug} className="rounded-lg border border-border bg-card p-4 shadow-sm">
                {card}
              </div>
            );
          }

          return (
            <Link
              key={guide.slug}
              to={guidePath}
              className="rounded-lg border border-border bg-card p-4 shadow-sm transition hover:border-primary/40"
            >
              {card}
            </Link>
          );
        })}
      </section>

      {summary.phases.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Por fase</h2>
          <div className="space-y-2">
            {summary.phases.map((phase) => (
              <div
                key={phase.phaseId}
                className="flex flex-col gap-2 rounded-md border border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium">{phase.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {phase.completedTasks}/{phase.totalTasks} tareas
                  </p>
                </div>
                <div className="flex min-w-[8rem] items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full ${progressBarClass(phase.percent)}`}
                      style={{ width: `${phase.percent}%` }}
                    />
                  </div>
                  <span className="w-10 text-right font-mono text-xs">{phase.percent}%</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
